import React, { useState, useEffect } from 'react';
import { Building2, Users, UserPlus, Shield, Trash2, Mail, Loader2, Crown, BarChart3, X, Check, Clock, UserCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

import { BACKEND_URL } from '../config/api';

export default function CompanyManagement() {
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviting, setInviting] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [acceptingInvite, setAcceptingInvite] = useState(null);

  useEffect(() => {
    fetchCompany();
    fetchPendingInvites();
  }, []);

  const fetchCompany = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company`, {
        withCredentials: true
      });
      setCompany(response.data);
      
      if (response.data.has_company) {
        fetchMembers();
      }
    } catch (error) {
      console.error('Failed to fetch company');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingInvites = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company/invites`, {
        withCredentials: true
      });
      setPendingInvites(response.data);
    } catch (error) {
      console.error('Failed to fetch invites');
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company/members`, {
        withCredentials: true
      });
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch members');
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/company/usage`, {
        withCredentials: true
      });
      setUsage(response.data);
    } catch (error) {
      toast.error('Failed to fetch usage data');
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!companyName.trim()) return;

    setCreating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/company/create`,
        { company_name: companyName },
        { withCredentials: true }
      );
      toast.success(`Company "${companyName}" created!`);
      setShowCreateModal(false);
      setCompanyName('');
      fetchCompany();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    } finally {
      setCreating(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/company/invite`,
        { email: inviteEmail, role: inviteRole },
        { withCredentials: true }
      );
      toast.success(`Invitation sent to ${inviteEmail}`);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (userId, memberName) => {
    if (!confirm(`Remove ${memberName} from the company?`)) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/company/members/${userId}`, {
        withCredentials: true
      });
      toast.success('Member removed');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/company/members/${userId}`,
        { role: newRole },
        { withCredentials: true }
      );
      toast.success('Role updated');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleAcceptInvite = async (inviteId, companyName) => {
    setAcceptingInvite(inviteId);
    try {
      await axios.post(
        `${BACKEND_URL}/api/company/invites/${inviteId}/accept`,
        {},
        { withCredentials: true }
      );
      toast.success(`You have joined ${companyName}!`);
      fetchCompany();
      fetchPendingInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setAcceptingInvite(null);
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/company/invites/${inviteId}/decline`,
        {},
        { withCredentials: true }
      );
      toast.success('Invitation declined');
      fetchPendingInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline invitation');
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      </div>
    );
  }

  // No company yet
  if (!company?.has_company) {
    return (
      <>
        {/* Pending Invitations */}
        {pendingInvites.length > 0 && (
          <div className="card mb-4" data-testid="pending-invites">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Mail className="h-5 w-5 text-orange-500" />
              Pending Invitations
            </h3>
            <div className="space-y-3">
              {pendingInvites.map((invite) => (
                <div
                  key={invite.invite_id}
                  className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200"
                  data-testid={`invite-${invite.invite_id}`}
                >
                  <div>
                    <p className="font-medium text-slate-900">{invite.company_name}</p>
                    <p className="text-sm text-slate-600">
                      Invited as <span className="font-medium">{invite.role === 'admin' ? 'Admin' : 'Team Member'}</span>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDeclineInvite(invite.invite_id)}
                      className="btn-secondary text-sm"
                      data-testid={`decline-invite-${invite.invite_id}`}
                    >
                      Decline
                    </button>
                    <button
                      onClick={() => handleAcceptInvite(invite.invite_id, invite.company_name)}
                      disabled={acceptingInvite === invite.invite_id}
                      className="btn-primary text-sm flex items-center gap-1"
                      data-testid={`accept-invite-${invite.invite_id}`}
                    >
                      {acceptingInvite === invite.invite_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <UserCheck className="h-4 w-4" />
                          Join
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card" data-testid="company-setup">
          <div className="text-center py-8">
            <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Multi-User Company Access</h3>
            <p className="text-slate-600 mb-4">
              Create a company to invite team members and share your chat allowance.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
              data-testid="create-company-btn"
            >
              <Building2 className="h-4 w-4 mr-2" />
              Create Company
            </button>
          </div>
        </div>

        {/* Create Company Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="sm:max-w-md" data-testid="create-company-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-orange-500" />
                Create Your Company
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateCompany} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  placeholder="e.g., Smith Construction Ltd"
                  className="w-full"
                  data-testid="company-name-input"
                />
              </div>
              <p className="text-sm text-slate-500">
                You'll be the company admin. You can invite up to 10 team members to share your chat allowance.
              </p>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !companyName.trim()}
                  className="btn-primary flex-1"
                  data-testid="confirm-create-company-btn"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Company'}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Has company
  return (
    <>
      <div className="card" data-testid="company-management">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-orange-500" />
              {company.name}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {company.member_count} team member{company.member_count !== 1 ? 's' : ''} • 
              {company.is_admin ? ' You are an admin' : ' You are a member'}
            </p>
          </div>
          {company.is_admin && (
            <div className="flex gap-2">
              <button
                onClick={() => { fetchUsage(); setShowUsageModal(true); }}
                className="btn-secondary text-sm flex items-center gap-2"
                data-testid="view-usage-btn"
              >
                <BarChart3 className="h-4 w-4" />
                Usage
              </button>
              <button
                onClick={() => setShowInviteModal(true)}
                className="btn-primary text-sm flex items-center gap-2"
                data-testid="invite-member-btn"
              >
                <UserPlus className="h-4 w-4" />
                Invite
              </button>
            </div>
          )}
        </div>

        {/* Shared Allowance */}
        <div className="p-4 bg-slate-50 rounded-lg mb-6">
          <p className="text-sm text-slate-600 mb-1">Shared Credit Allowance</p>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-slate-900">
              {(company.subscription?.chats_remaining || 0) + (company.subscription?.topup_balance || 0)}
            </span>
            <span className="text-slate-500">credits remaining</span>
          </div>
          <div className="flex gap-4 mt-2 text-sm">
            <span className="text-slate-600">
              <span className="font-medium">{company.subscription?.chats_remaining || 0}</span> included
            </span>
            <span className="text-slate-600">
              <span className="font-medium">{company.subscription?.topup_balance || 0}</span> top-up
            </span>
          </div>
        </div>

        {/* Team Members */}
        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Users className="h-4 w-4" />
          Team Members
        </h4>
        <div className="space-y-2">
          {members.map((member) => (
            <div
              key={member.user_id}
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
              data-testid={`member-${member.user_id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                  {member.role === 'admin' ? (
                    <Crown className="h-5 w-5 text-yellow-500" />
                  ) : (
                    <Users className="h-5 w-5 text-slate-400" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-slate-900">{member.name}</p>
                  <p className="text-sm text-slate-500">{member.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  member.role === 'admin' 
                    ? 'bg-yellow-100 text-yellow-700' 
                    : 'bg-slate-200 text-slate-600'
                }`}>
                  {member.role === 'admin' ? 'Admin' : 'Member'}
                </span>
                {company.is_admin && member.user_id !== company.owner_id && (
                  <div className="flex gap-1">
                    <select
                      value={member.role}
                      onChange={(e) => handleUpdateRole(member.user_id, e.target.value)}
                      className="text-xs py-1"
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button
                      onClick={() => handleRemoveMember(member.user_id, member.name)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                      title="Remove member"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="sm:max-w-md" data-testid="invite-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-orange-500" />
              Invite Team Member
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Mail className="h-4 w-4 inline mr-1" />
                Email Address *
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                placeholder="colleague@company.com"
                className="w-full"
                data-testid="invite-email-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                They must already have an OnsiteHand employer account
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Shield className="h-4 w-4 inline mr-1" />
                Role
              </label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="w-full"
                data-testid="invite-role-select"
              >
                <option value="member">Team Member - Can search & message tradespeople</option>
                <option value="admin">Admin - Can also manage team & billing</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowInviteModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={inviting || !inviteEmail.trim()}
                className="btn-primary flex-1"
                data-testid="send-invite-btn"
              >
                {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send Invitation'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Usage Modal */}
      <Dialog open={showUsageModal} onOpenChange={setShowUsageModal}>
        <DialogContent className="sm:max-w-lg" data-testid="usage-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-orange-500" />
              Chat Usage This Month
            </DialogTitle>
          </DialogHeader>
          {usage ? (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-700">{usage.total_chats_used}</p>
                  <p className="text-xs text-blue-600">Credits Used</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">{usage.chats_remaining}</p>
                  <p className="text-xs text-green-600">Remaining</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-orange-700">{usage.topup_balance}</p>
                  <p className="text-xs text-orange-600">Top-Up</p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Usage by Member</h4>
                <div className="space-y-2">
                  {usage.usage_by_member.map((member) => (
                    <div
                      key={member.user_id}
                      className="flex items-center justify-between p-2 bg-slate-50 rounded"
                    >
                      <span className="text-sm text-slate-700">{member.name}</span>
                      <span className="text-sm font-medium text-slate-900">
                        {member.chats_used} chats
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
